import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { hasKnownEntityId, hasKnownShowIdentity } from '../utils/entityIdentity';
import FollowOutline from './FollowOutline';
import PreferenceStatusButtons from './PreferenceStatusButtons';
import ShowPreferenceControl from './ShowPreferenceControl';

export const PREFERENCE_NEGATIVE_CONTENT_OPACITY = 0.45;

export const analyticsEventName = (status) => (
    status === true ? 'subscribe_true' : status === false ? 'subscribe_false' : 'subscribe_null'
);

export const sourceImageURL = (source) => {
    const imageURL = source?.largeImageURL || source?.imageURL || '';
    return imageURL.replace('/srcimg%2F', '/srcimg_large%2F');
};

const PreferenceRowMain = ({ onOpen, to, label, children }) => (
    to ? <Link className='settingsPreferenceMain' to={to} aria-label={label}>{children}</Link> : onOpen ? (
        <button type='button' className='settingsPreferenceMain' onClick={onOpen} aria-label={label}>
            {children}
        </button>
    ) : (
        <div className='settingsPreferenceMain'>{children}</div>
    )
);

const PreferenceArtwork = ({ imageURL, fallbackImageURL, name, className, style }) => {
    const [failed, setFailed] = useState([]);
    useEffect(() => setFailed([]), [imageURL, fallbackImageURL]);
    const image = [imageURL, fallbackImageURL].find(url => url && !failed.includes(url));
    return image ? <img src={image} alt={name} className={className} style={style}
        onError={() => setFailed(current => [...current, image])} /> :
        <span className={`${className} settingsPreferenceArtworkFallback`} style={style}>{name?.charAt(0) || '?'}</span>;
};

export const TopicPreferenceRow = ({ topicId, topicStatus, setTopicStatus, onOpen, control, testId = `topic-status-${topicId}` }) => {
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);

    const changeStatus = async (nextStatus) => {
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        try {
            await setTopicStatus(topicId, nextStatus);
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    return (
        <div className='settingsPreferenceRow settingsTopicPreferenceRow' role='group' aria-label={topicId} aria-busy={saving}>
            <PreferenceRowMain onOpen={onOpen ? () => onOpen(topicId) : undefined} label={`Search ${topicId}`}>
                <span className='settingsPreferenceName'>{topicId}</span>
            </PreferenceRowMain>
            {control || <PreferenceStatusButtons
                status={topicStatus}
                onStatusChange={changeStatus}
                disabled={saving}
                variant='thumbPair'
                className='settingsPreferenceRowControl'
                positiveLabel='More like this'
                selectedPositiveLabel='More like this'
                negativeLabel='Less like this'
                testId={testId}
            />}
        </div>
    );
};

export const SpeakerPreferenceRow = ({ speaker, status, onStatusChange, onOpen, description, disabled = false, showControl = true, to, controlLabels = {} }) => {
    const artworkStyle = { opacity: status === false ? PREFERENCE_NEGATIVE_CONTENT_OPACITY : 1 };
    const artworkClassName = 'settingsPreferenceArtwork settingsSpeakerPreferenceArtwork';
    const followed = hasKnownEntityId(speaker.id) && status === true;

    return (
        <div className='settingsPreferenceRow settingsSpeakerPreferenceRow' role='group' aria-label={speaker.name}>
            <PreferenceRowMain onOpen={onOpen} to={to} label={`${speaker.name}${followed ? ", followed" : ""}`}>
                <FollowOutline followed={followed} circular>
                    <PreferenceArtwork imageURL={speaker.imageURL} name={speaker.name}
                        className={artworkClassName} style={artworkStyle} />
                </FollowOutline>
                <span className='settingsPreferenceText'>
                    <span className='settingsPreferenceName'>{speaker.name}</span>
                    {description && <span className='settingsPreferenceDescription'>{description}</span>}
                </span>
            </PreferenceRowMain>
            {showControl && <PreferenceStatusButtons
                status={status}
                onStatusChange={onStatusChange}
                disabled={disabled}
                variant='thumbPair'
                className='settingsPreferenceRowControl'
                positiveLabel='More like this'
                selectedPositiveLabel='More like this'
                negativeLabel='Less like this'
                {...controlLabels}
            />}
        </div>
    );
};

export const ShowPreferenceRow = ({
    source, status, onStatusChange, onOpen, mutation, onRetry,
    description, openLabel = source.name, showControl = true, to, controlLabels = {},
}) => {
    const imageURL = sourceImageURL(source);
    const artworkStyle = { opacity: status === false ? PREFERENCE_NEGATIVE_CONTENT_OPACITY : 1 };
    const followed = hasKnownShowIdentity(source) && status === true;
    return (
        <div className='settingsPreferenceRow settingsShowPreferenceRow' role='group' aria-label={source.name}>
            <PreferenceRowMain onOpen={onOpen} to={to} label={`${openLabel}${followed ? ", followed" : ""}`}>
                <FollowOutline followed={followed}>
                    <PreferenceArtwork imageURL={imageURL} fallbackImageURL={source.imageURL} name={source.name}
                        className='settingsPreferenceArtwork' style={artworkStyle} />
                </FollowOutline>
                <span className='settingsPreferenceText'>
                    <span className='settingsPreferenceName'>{source.name}</span>
                    {description && <span className='settingsPreferenceDescription'>{description}</span>}
                </span>
            </PreferenceRowMain>
            {showControl && <ShowPreferenceControl
                status={status}
                onStatusChange={onStatusChange}
                mutation={mutation}
                onRetry={onRetry}
                variant='thumbPair'
                className='settingsPreferenceRowControl'
                positiveLabel='More like this'
                selectedPositiveLabel='More like this'
                negativeLabel='Less like this'
                {...controlLabels}
            />}
        </div>
    );
};
